<template>
  <v-app>
      <template v-if="!error">
          <v-overlay
            :model-value="isNewVersionAvailable"
            class="upgrade-overlay"
          >
              <v-banner
                  color="warning"
                  icon="mdi-update"
                  :lines="$vuetify.display.mdAndUp ? 'one' : undefined"
                  sticky
              >
                <template #text>
                  Dostupna je nova verzija aplikacije. Molimo vas da osvježite stranicu
                  kako bi nastavili koristiti ODG Web.
                </template>

                <template #actions>
                  <v-btn
                    size="large"
                    @click="onHardRefresh"
                  >
                    Osvježi
                  </v-btn>
                </template>
              </v-banner>
          </v-overlay>

          <Layout />
          <ModalOverlay />
      </template>
      <v-empty-state
          v-else
          headline="Neočekivana greška!"
          title="Došlo je do greške"
          text="Molimo vas da osvježite stranicu ili pokušate ponovno kasnije."
          class="my-4 w-100 h-100"
      >
        <template #text>
          <div class="error-text pa-2">
            <p>
              Molimo vas da osvježite stranicu ili pokušate ponovno kasnije.
            </p>
  
            <p>
              Možete nas kontaktirati sa podacima o grešci prikazanima ispod.
            </p>
  
            <code class="code-block mb-2">{{ error.message }}</code>
  
            <code class="code-block mb-2">{{ error.stack }}</code>

            <v-btn
                color="primary"
                block
                variant="outlined"
                class="mt-8"
                @click="onRefresh"
            >
              Osvježi stranicu
            </v-btn>

            <v-btn
                color="secondary"
                block
                variant="outlined"
                to="/"
                class="mt-4"
                @click="delayedHardRefresh"
            >
              Početna stranica
            </v-btn>
          </div>
        </template>
      </v-empty-state>
  </v-app>
</template>

<script setup lang="ts">
import { onErrorCaptured, onScopeDispose, ref } from 'vue';
import Layout from './components/base/Layout.vue';
import { ModalCode } from './modals/ModalCode';
import ModalOverlay from './modals/ModalOverlay.vue';
import { useModal } from './modals/useModal';
import { conditionallyRunWithRetry } from './utils/conditionallyRunWithRetry';
import { canInstallPwa, registerPwaInstallListener } from './utils/pwa';
import { useRouter } from 'vue-router';
import { useVersionChecker } from './composition/useVersionChecker';
import { logError } from './utils/logger';
import { isChunkLoadError, tryReloadOnChunkError } from './utils/handleChunkLoadError';
import { useAnalytics } from './composition/useAnalytics';
import { analyticsService } from './services/AnalyticsService';
import { installWebVitalsReporter } from './utils/webVitalsReporter';
import { client } from './services/api/client';

const error = ref<Error>();

const modal = useModal();
const router = useRouter();

onErrorCaptured((err) => {
  if (isChunkLoadError(err) && tryReloadOnChunkError(router.currentRoute.value.fullPath)) {
    return false;
  }

  logError(err);
  error.value = err;
  return false;
});

const { isNewVersionAvailable } = useVersionChecker();

function onRefresh() {
  router.go(0);
}

function onHardRefresh() {
  location.reload();
}

function delayedHardRefresh() {
  setTimeout(onHardRefresh, 500);
}

registerPwaInstallListener();


conditionallyRunWithRetry(
  showPwaInstallModal,
  canInstallPwa,
  10,
  1_000,
);

function showPwaInstallModal() {
  modal.showModal(ModalCode.PwaInstall);
}

// Analytics bootstrap. Installs the consent reconciler, the visibility
// /pagehide flush hooks, drains anything spilled to localStorage from a
// previous session, and starts the per-route impression stream.
//
// We intentionally do not fire an explicit `route_view` for the route that
// was already resolved by the time App.vue mounts — that would race the
// consent reconcile and risk popping the modal against a server-side
// preference we haven't read yet. The next navigation picks up cleanly.
const analytics = useAnalytics();
analytics.installAuthWatcher();
// Subscribe to web-vitals BEFORE installing the lifecycle flush handlers so
// the metric listeners run first on `visibilitychange → hidden`. The flush
// `pagehide` backup covers us if module resolution lands later.
void installWebVitalsReporter();
analyticsService.installLifecycleHandlers();
void analyticsService.drainPersistedQueue();

// Mirror the Supabase access token into the analytics service so the
// unload-time beacon can attach the user JWT. Without this the beacon falls
// back to the anon key, the edge function resolves `user_id` to null, and
// `analytics_forget_me` later misses those rows. We hydrate from the in-
// memory session up front and keep listening for refreshes / sign-in /
// sign-out events.
void client.auth.getSession().then(({ data }) => {
  analyticsService.setAccessToken(data.session?.access_token ?? null);
}).catch((error) => {
  logError(error, { source: 'analytics:hydrate-access-token' });
});
// Both subscriptions below are captured so HMR (or any future remount) can
// dispose them.
const { data: authSubscription } = client.auth.onAuthStateChange((_event, session) => {
  analyticsService.setAccessToken(session?.access_token ?? null);
});

const stopRouteTracking = router.afterEach((to) => {
  analytics.trackImpression('route_view', {
    path: to.path,
    name: to.name?.toString(),
  });
});

onScopeDispose(() => {
  stopRouteTracking();
  authSubscription.subscription.unsubscribe();
});
</script>

<style scoped>

.error-text {
  width: 100%;
  max-width: 100vw;
}

.error-text p {
  margin-bottom: 16px;
}

.code-block {
    display: block;
    white-space: pre-wrap;
    word-break: break-all;
    width: 100%;
    max-width: calc(100vw - 16px);
    color: rgba(var(--v-theme-error));
    text-align: left;
}

.upgrade-overlay :deep(.v-overlay__content) {
  width: 100vw;
}
</style>
